import { callCloudFunction, getFirebaseFunctionUrl } from '../../app/utils/firebaseFunctions';
import { schoolAuth } from './schoolFirebase';

/** Error codes returned by serverless/functions/src/schoolService.ts */
export type SchoolApiErrorCode =
  | 'missing_codes'
  | 'invalid_codes'
  | 'student_inactive'
  | 'unauthorized'
  | 'forbidden'
  | 'missing_audio'
  | 'no_current_pasuk'
  | 'unsupported_book'
  | 'rabbi_audio_missing'
  | 'server_error';

export class SchoolApiError extends Error {
  code: SchoolApiErrorCode;

  constructor(code: SchoolApiErrorCode) {
    super(code);
    this.code = code;
  }
}

async function readError(res: Response): Promise<SchoolApiError> {
  try {
    const body: unknown = await res.json();
    const code =
      body && typeof body === 'object' && typeof (body as { error?: unknown }).error === 'string'
        ? ((body as { error: string }).error as SchoolApiErrorCode)
        : 'server_error';
    return new SchoolApiError(code);
  } catch {
    return new SchoolApiError('server_error');
  }
}

/**
 * Links the currently signed-in (anonymous) user to a student: on valid codes the
 * function writes schoolSessions/{uid}, which the security rules use from then on.
 */
export async function linkStudentSession(classCode: string, studentCode: string): Promise<void> {
  const user = schoolAuth.currentUser;
  if (!user) throw new SchoolApiError('unauthorized');
  const idToken = await user.getIdToken();
  const res = await callCloudFunction(
    'schoolStudentLogin',
    getFirebaseFunctionUrl('schoolStudentLogin'),
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
      body: JSON.stringify({ classCode, studentCode }),
    },
  );
  if (!res.ok) throw await readError(res);
}

export interface AttemptResult {
  saved: boolean;
  score: number;
  explanation: string;
  audioUrl?: string;
}

export async function submitAttempt(audioBase64: string, language: string): Promise<AttemptResult> {
  const user = schoolAuth.currentUser;
  if (!user) throw new SchoolApiError('unauthorized');
  const idToken = await user.getIdToken();
  const res = await callCloudFunction(
    'schoolSubmitAttempt',
    getFirebaseFunctionUrl('schoolSubmitAttempt'),
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
      body: JSON.stringify({ audioBase64, language }),
    },
  );
  if (!res.ok) throw await readError(res);
  const data = (await res.json()) as Partial<AttemptResult>;
  return {
    saved: data.saved === true,
    score: typeof data.score === 'number' ? data.score : 0,
    explanation: typeof data.explanation === 'string' ? data.explanation : '',
    audioUrl: data.audioUrl,
  };
}
